import { ApiError, type OperationKey } from '@estudio/contracts';
import { createCloudClient, type Transport } from '@estudio/cloud-client';
import { createMemoryDraftStore, createMockBridge, type AuthPort, type AuthSession } from '@estudio/desktop-contract';
import { canonicalJson, emptyPlan } from '@estudio/domain';
import type { Services } from '../core/services.tsx';

/**
 * Modo demostración: backend en memoria con datos simulados, separado de
 * cualquier cuenta real (nunca llama a la nube). La UI muestra permanentemente
 * «datos simulados». Operaciones no cubiertas responden CAPABILITY_UNAVAILABLE.
 */
type Row = Record<string, unknown> & { id: string };
const now = () => new Date().toISOString();
const uid = () => crypto.randomUUID();
const hash = (v: unknown) => {
  let h = 0;
  const s = canonicalJson(v);
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16).padStart(8, '0').repeat(8);
};

function unavailable(key: string): never {
  throw new ApiError({ code: 'CAPABILITY_UNAVAILABLE', message: `«${key}» no está disponible en el modo demostración.`, retryable: false });
}

export function createDemoBackend() {
  const ws = { id: uid(), name: 'Espacio de demostración', role: 'owner', timezone: 'America/Bogota', status: 'active', change_seq: 0 };
  const channels: Row[] = [];
  const profiles: Row[] = [];
  const versions: Row[] = [];
  const rules: Row[] = [];
  const projects: Row[] = [];
  const documents: Row[] = [];
  const scriptEntries: Row[] = [];
  const scriptAssignments: Row[] = [];
  const revisions: Row[] = [];
  const conflicts: Row[] = [];
  const comments: Row[] = [];
  const approvals: Row[] = [];
  const assets: Row[] = [];
  const jobs: Row[] = [];
  const takes: Row[] = [];
  const variants: Row[] = [];
  const deliverables: Row[] = [];
  const receipts = new Map<string, { h: string; r: unknown }>();

  const meta = (r: Row) => ({ id: r.id, document_id: r.document_id, number: r.number, branch: r.branch, parent_revision_id: r.parent_revision_id ?? null, content_hash: r.content_hash, byte_size: 1, source: r.source ?? 'human', author_id: null, device_id: null, created_at: r.created_at });

  function createChannel(name: string, line: string) {
    const c: Row = { id: uid(), workspace_id: ws.id, name, line, language: 'es', audience: '', timezone: ws.timezone, identity: {}, publishing_policy: { pieces_per_day: 1, slots: [] }, active_edit_profile_version_id: null, archived_at: null, created_at: now() };
    for (const kind of ['edit', 'marketing', 'language']) {
      const p: Row = { id: uid(), workspace_id: ws.id, channel_id: c.id, kind, name: `${kind} · ${name}` };
      const v: Row = { id: uid(), workspace_id: ws.id, profile_id: p.id, version: 1, status: 'approved', notes: 'Plantilla de demostración; valores por calibrar.', approved_at: now() };
      profiles.push(p);
      versions.push(v);
      if (kind === 'edit') {
        c.active_edit_profile_version_id = v.id;
        rules.push({ id: uid(), profile_version_id: v.id, rule_key: 'subs.max_lineas', family: 'subtitles', scope: 'any', severity: 'warning', parameter: 'max_lineas', criterion: 'Máximo de líneas por subtítulo.', value: 2, unit: 'lineas', calibration: 'por_calibrar', example: '', counterexample: '', reason: 'Lectura cómoda.', author_kind: 'process' });
        if (line === 'anime') rules.push({ id: uid(), profile_version_id: v.id, rule_key: 'avatar.actuaciones', family: 'avatar', scope: 'any', severity: 'preference', parameter: 'actuaciones', criterion: 'El perrito alterna actuaciones con semilla persistente.', value: ['asomarse', 'reposo'], unit: null, calibration: 'no_aplica', example: '', counterexample: '', reason: '', author_kind: 'process' });
      }
    }
    channels.push(c);
    return c;
  }

  function projectJson(p: Row) {
    const c = channels.find((x) => x.id === p.channel_id)!;
    return {
      ...p,
      channel: { id: c.id, name: c.name, line: c.line, timezone: c.timezone },
      script_document_id: documents.find((d) => d.project_id === p.id && d.kind === 'script' && !d.deleted_at)?.id ?? null,
      edit_plan_document_id: documents.find((d) => d.project_id === p.id && d.kind === 'edit_plan' && !d.deleted_at)?.id ?? null,
      approvals: approvals.filter((a) => a.project_id === p.id && !a.invalidated_at),
      open_conflicts: conflicts.filter((x) => x.status === 'open' && documents.find((d) => d.id === x.document_id)?.project_id === p.id).length,
    };
  }

  function createProject(channel_id: string, title: string, format: string) {
    const c = channels.find((x) => x.id === channel_id)!;
    const p: Row = { id: uid(), workspace_id: ws.id, channel_id, title, format, stage: 'brief', brief: { schema_version: 1 }, profile_version_id: c.active_edit_profile_version_id, status: 'active', created_at: now(), updated_at: now() };
    projects.push(p);
    documents.push({ id: uid(), workspace_id: ws.id, project_id: p.id, kind: 'script', title: `Guion · ${title}`, head_revision_id: null, deleted_at: null });
    documents.push({ id: uid(), workspace_id: ws.id, project_id: p.id, kind: 'edit_plan', title: `Plan · ${title}`, head_revision_id: null, deleted_at: null });
    return p;
  }

  function saveDoc(doc: Row, base: string | null, content: unknown, source = 'human', force = false) {
    const h = hash(content);
    const head = revisions.find((r) => r.id === doc.head_revision_id);
    if (head && head.content_hash === h && !force) return { status: 'unchanged', document_id: doc.id, revision: meta(head) };
    const rev: Row = { id: uid(), workspace_id: ws.id, document_id: doc.id, parent_revision_id: base, number: revisions.filter((r) => r.document_id === doc.id).length + 1, branch: 'main', content, content_hash: h, source, created_at: now() };
    if ((base ?? null) === (doc.head_revision_id ?? null) && !force) {
      revisions.push(rev);
      doc.head_revision_id = rev.id;
      const entry = scriptEntries.find(s => s.id === doc.id);
      if (entry) entry.updated_at = now();
      return { status: 'saved', document_id: doc.id, revision: meta(rev) };
    }
    rev.branch = 'proposal';
    revisions.push(rev);
    const c: Row = { id: uid(), document_id: doc.id, base_revision_id: base, head_revision_id: doc.head_revision_id, proposal_revision_id: rev.id, status: 'open', created_at: now() };
    conflicts.push(c);
    return { status: 'conflict', code: 'REVISION_CONFLICT', document_id: doc.id, conflict_id: c.id, head_revision: head ? meta(head) : null, proposal_revision: meta(rev) };
  }

  // Datos iniciales sintéticos.
  const anime = createChannel('Anime (demo)', 'anime');
  createChannel('Gaming (demo)', 'gaming');
  const p1 = createProject(anime.id, 'El héroe que no quería ganar', 'short');
  const sdoc = documents.find((d) => d.project_id === p1.id && d.kind === 'script')!;
  saveDoc(sdoc, null, { schema_version: 1, type: 'doc', blocks: [
    { id: 'blk_demo01', type: 'paragraph', text: 'Hay héroes que no quieren ganar.', node: { type: 'paragraph', content: [{ type: 'text', text: 'Hay héroes que no quieren ganar.' }] } },
    { id: 'blk_demo02', type: 'paragraph', text: 'Este texto es de demostración: no es un guion real.', node: { type: 'paragraph', content: [{ type: 'text', text: 'Este texto es de demostración: no es un guion real.' }] } },
  ] });
  const pdoc = documents.find((d) => d.project_id === p1.id && d.kind === 'edit_plan')!;
  scriptEntries.push({ id: sdoc.id, document_id: sdoc.id, workspace_id: ws.id, channel_id: anime.id,
    title: p1.title, format: p1.format, brief: p1.brief, status: 'active', metadata_version: 1,
    legacy_project_id: p1.id, updated_at: now() });
  saveDoc(pdoc, null, emptyPlan('short', String(p1.profile_version_id)));
  for (const [t, kind] of [['Tema principal (demo)', 'music'], ['Golpe seco (demo)', 'sfx'], ['Clip ausente (demo)', 'video']] as const) {
    assets.push({ id: uid(), workspace_id: ws.id, kind, title: t, origin: 'own', description: 'Ficha sintética de demostración.', tags: ['demo'], created_at: now(),
      versions: [{ id: uid(), version_label: 'v1', sha256: null, sha256_status: 'unknown', bytes: null, bytes_status: 'unknown', mime: null, duration_value: null, duration_unit: null, duration_status: 'unknown', fps_num: null, fps_den: null, sample_rate: null, loudness_lufs: null, loudness_status: 'unknown' }],
      availability: [], rights_status: null, open_incidents: 0 });
  }

  function once(input: Record<string, unknown>, key: string, fn: () => unknown) {
    const op = input.operation_id as string | undefined;
    if (!op) return fn();
    const h = canonicalJson({ ...input, operation_id: undefined, key });
    const prior = receipts.get(op);
    if (prior) {
      if (prior.h !== h) throw new ApiError({ code: 'IDEMPOTENCY_MISMATCH', message: 'operation_id reutilizado con otro contenido', retryable: false });
      return prior.r;
    }
    const r = fn();
    receipts.set(op, { h, r });
    return r;
  }

  const scriptJson = (s: Row) => {
    const d = documents.find(d => d.id === s.id) ?? unavailable('guion');
    const r = revisions.find(r => r.id === d.head_revision_id);
    const blocks = (r?.content as {blocks?:{text:string}[]}|undefined)?.blocks ?? [];
    return { ...s, head_revision: r ? meta(r) : null, preview: blocks.map(b => b.text).join('\n').slice(0, 300),
      has_text: blocks.some(block => block.text.trim().length > 0),
      revision_count: revisions.filter(revision => revision.document_id === s.id).length };
  };
  const conflict = (message: string): never => { throw new ApiError({code:'CONFLICT',message,retryable:false}); };

  const handlers: Partial<Record<OperationKey, (i: Record<string, unknown>) => unknown>> = {
    'workspace.bootstrap': () => ({ user_id: 'demo', workspaces: [ws] }),
    'workspace.get': () => ({ workspace: ws, role: 'owner', quota: { remote_bytes_limit: 5 * 1024 ** 3, bytes_reserved: 0, bytes_used: 0, records_limit: 20000, records_used: assets.length, bytes_alert: false } }),
    'workspace.channel_list': () => ({ items: channels.filter((c) => !c.archived_at) }),
    'workspace.channel_create': (i) => ({ channel: createChannel(String(i.name), String(i.line)) }),
    'workspace.channel_update': (i) => {
      const c = channels.find((x) => x.id === i.channel_id)!;
      Object.assign(c, Object.fromEntries(Object.entries(i).filter(([k]) => !['channel_id', 'operation_id', 'archived'].includes(k))));
      if (i.archived !== undefined) c.archived_at = i.archived ? now() : null;
      return { channel: c };
    },
    'workspace.profile_list': (i) => ({ items: profiles.filter((p) => !i.channel_id || p.channel_id === i.channel_id).map((p) => ({ ...p, versions: versions.filter((v) => v.profile_id === p.id) })) }),
    'workspace.profile_version_get': (i) => {
      const v = versions.find((x) => x.id === i.profile_version_id)!;
      return { version: v, profile: profiles.find((p) => p.id === v.profile_id), rules: rules.filter((r) => r.profile_version_id === v.id), examples: [], proposals: [] };
    },
    'workspace.project_list': (i) => ({ items: projects.filter((p) => (!i.channel_id || p.channel_id === i.channel_id) && (!i.format || p.format === i.format) && (!i.stage || p.stage === i.stage) && p.status === (i.status ?? 'active')).map(projectJson), next_cursor: null }),
    'workspace.project_get': (i) => ({ project: projectJson(projects.find((p) => p.id === i.project_id) ?? unavailable('proyecto')) }),
    'workspace.script_list': (i) => {
      const entries = scriptEntries.filter(s => (!i.channel_id || s.channel_id === i.channel_id) && (!i.status || s.status === i.status)
        && (!i.format || s.format === i.format)
        && (!i.content || scriptJson(s).has_text === (i.content === 'written'))
        && (!i.q || String(s.title).toLocaleLowerCase('es').includes(String(i.q).toLocaleLowerCase('es'))))
        .sort((a,b) => String(b.updated_at).localeCompare(String(a.updated_at)) || a.id.localeCompare(b.id));
      const start = i.cursor ? Number(i.cursor) : 0, limit = Number(i.limit ?? 100);
      return {items:entries.slice(start,start+limit).map(scriptJson),next_cursor:start+limit<entries.length?String(start+limit):null};
    },
    'workspace.script_get': (i) => ({script:scriptJson(scriptEntries.find(s => s.id === i.document_id) ?? unavailable('guion'))}),
    'workspace.script_usage': (i) => ({items:(i.document_ids as string[]).map(document_id => {
      const entry = scriptEntries.find(s => s.id === document_id) ?? unavailable('guion');
      const ids = new Set([entry.legacy_project_id, ...scriptAssignments.filter(a => a.document_id === document_id).map(a => a.project_id)]);
      const related = projects.filter(p => ids.has(p.id) && p.channel_id === entry.channel_id && !p.deleted_at);
      return {document_id,metrics_visible:true,truncated:related.length>20,projects:related.slice(0,20).map(p => {
        const source = projects.find(source => source.id === p.source_project_id && source.channel_id === p.channel_id && !source.deleted_at);
        const assignment = [...scriptAssignments].reverse().find(a => a.document_id === document_id && a.project_id === p.id);
        const revision = assignment && revisions.find(r => r.id === assignment.revision_id && r.document_id === document_id);
        return {project_id:p.id,title:p.title,format:p.format,status:p.status,
          source_project:source?{id:source.id,title:source.title}:null,
          source_revision:revision?{id:revision.id,number:revision.number}:null,
          views:null,views_covered:0,publication_count:0,platforms:[]};
      })};
    })}),
    'workspace.script_create': (i) => {
      const d:Row = {id:uid(),workspace_id:ws.id,project_id:null,kind:'script',title:i.title,head_revision_id:null,deleted_at:null};
      documents.push(d);
      const s:Row = {id:d.id,document_id:d.id,workspace_id:ws.id,channel_id:i.channel_id,title:i.title,format:i.format,
        brief:i.brief??{schema_version:1},status:'active',metadata_version:1,legacy_project_id:null,updated_at:now()};
      scriptEntries.push(s); if(i.content) saveDoc(d,null,i.content);
      return {script:scriptJson(s)};
    },
    'workspace.script_update': (i) => {
      const s = scriptEntries.find(s => s.id === i.document_id) ?? unavailable('guion');
      if(s.metadata_version !== i.expected_metadata_version) conflict('El guion cambió (demo).');
      for(const key of ['title','format','brief','status']) if(i[key] !== undefined) s[key] = i[key];
      s.metadata_version = Number(s.metadata_version)+1; s.updated_at = now();
      return {script:scriptJson(s)};
    },
    'workspace.script_assign': (i) => {
      const s = scriptEntries.find(s => s.id === i.document_id) ?? unavailable('guion');
      const p = projects.find(p => p.id === i.project_id) ?? unavailable('proyecto');
      const r = revisions.find(r => r.id === i.revision_id && r.document_id === s.id && r.branch === 'main') ?? unavailable('revisión');
      if(p.channel_id !== s.channel_id) conflict('El guion pertenece a otro canal (demo).');
      let d = documents.find(d => d.project_id === p.id && d.kind === 'script' && !d.deleted_at);
      if((d?.head_revision_id ?? null) !== i.expected_head_revision_id) conflict('El guion del proyecto cambió (demo).');
      if(d?.id === s.id) conflict('El proyecto ya contiene este guion (demo).');
      if(d && scriptEntries.some(entry => entry.id === d!.id)){d.project_id = null; d = undefined;}
      if(!d){d = {id:uid(),workspace_id:ws.id,project_id:p.id,kind:'script',title:`Guion · ${String(p.title)}`,head_revision_id:null,deleted_at:null};documents.push(d);}
      const saved = saveDoc(d,d.head_revision_id as string|null,structuredClone(r.content));
      const working = revisions.find(rev => rev.id === d!.head_revision_id)!;
      const assignment = {id:uid(),workspace_id:ws.id,project_id:p.id,document_id:s.id,revision_id:r.id,
        working_document_id:d.id,working_revision_id:working.id,assigned_at:now()};
      scriptAssignments.push(assignment);
      return {assignment,save:saved};
    },
    'workspace.script_sync_status': () => ({settings:null}),
    'workspace.script_sync_list': () => ({items:[],next_cursor:null}),
    'workspace.project_create': (i) => {
      const p = createProject(String(i.channel_id), String(i.title), String(i.format));
      p.source_project_id = i.source_project_id ?? null;
      return { project: projectJson(p) };
    },
    'workspace.project_family': (i) => {
      const p = projects.find((x) => x.id === i.project_id) ?? unavailable('proyecto');
      const parent = projects.find((x) => x.id === p.source_project_id);
      return { parent: parent ? projectJson(parent) : null, children: projects.filter((x) => x.source_project_id === p.id).map(projectJson) };
    },
    'workspace.project_update': (i) => {
      const p = projects.find((x) => x.id === i.project_id)!;
      for (const k of ['title', 'stage', 'brief', 'status', 'source_project_id'] as const) if (i[k] !== undefined) p[k] = i[k];
      p.updated_at = now();
      return { project: projectJson(p) };
    },
    'workspace.voice_list': (i) => ({ items: takes.filter((t) => t.project_id === i.project_id && t.status !== 'discarded').sort((a, b) => Number(a.position) - Number(b.position)) }),
    'workspace.voice_import': (i) => {
      const t: Row = { id: uid(), project_id: i.project_id, position: takes.length, label: i.label, transcript: i.transcript ?? null, transcript_source: i.transcript ? 'imported' : 'none', duration_status: 'unknown', sample_rate: i.sample_rate ?? null, duration_samples: null, status: 'imported' };
      takes.push(t);
      return { take: t };
    },
    'workspace.voice_update': (i) => {
      const t = takes.find((x) => x.id === i.take_id)!;
      if (i.transcript) Object.assign(t, { transcript: i.transcript, transcript_source: 'manual', status: 'corrected' });
      if (i.status) t.status = i.status;
      return { take: t };
    },
    'workspace.voice_reorder': (i) => {
      (i.ordered_take_ids as string[]).forEach((id, k) => {
        const t = takes.find((x) => x.id === id);
        if (t) t.position = k;
      });
      return { ordered_take_ids: i.ordered_take_ids };
    },
    'document.get': (i) => {
      const d = documents.find((x) => x.id === i.document_id)!;
      const h = revisions.find((r) => r.id === d.head_revision_id);
      return {
        document: d,
        head_revision: h ? { ...meta(h), content: h.content } : null,
        open_conflicts: conflicts.filter((c) => c.document_id === d.id && c.status === 'open').map((c) => ({ ...c, proposal_revision: meta(revisions.find((r) => r.id === c.proposal_revision_id)!) })),
        server_time: now(),
      };
    },
    'document.save': (i) => {
      const d = documents.find((x) => x.id === i.document_id)!;
      if (d.deleted_at) throw new ApiError({ code: 'DOCUMENT_DELETED', message: 'Eliminado', retryable: false });
      return saveDoc(d, (i.base_revision_id as string | null) ?? null, i.content);
    },
    'document.import_proposal': (i) => saveDoc(documents.find((x) => x.id === i.document_id)!, (i.base_revision_id as string | null) ?? null, i.content, 'agent', true),
    'document.history': (i) => ({ items: revisions.filter((r) => r.document_id === i.document_id).map(meta).reverse(), next_cursor: null }),
    'document.revision_get': (i) => {
      const r = revisions.find((x) => x.id === i.revision_id)!;
      return { revision: { ...meta(r), content: r.content } };
    },
    'document.resolve': (i) => {
      const c = conflicts.find((x) => x.id === i.conflict_id)!;
      const d = documents.find((x) => x.id === c.document_id)!;
      if (i.action === 'keep_current') c.status = 'kept_current';
      else {
        const content = i.action === 'adopt_proposal' ? revisions.find((r) => r.id === c.proposal_revision_id)!.content : i.merged_content;
        saveDoc(d, d.head_revision_id as string | null, content);
        c.status = i.action === 'adopt_proposal' ? 'adopted' : 'merged';
      }
      return { status: c.status, document_id: d.id };
    },
    'document.comment_list': (i) => ({ items: comments.filter((c) => c.document_id === i.document_id && (!i.revision_id || c.revision_id === i.revision_id)) }),
    'document.comment_create': (i) => {
      const c: Row = { id: uid(), document_id: i.document_id, revision_id: i.revision_id, block_id: i.block_id ?? null, scene_id: i.scene_id ?? null, anchor: i.anchor ?? {}, body: i.body, author_kind: 'human', created_at: now(), resolved_at: null };
      comments.push(c);
      return { comment: c };
    },
    'document.comment_resolve': (i) => {
      const c = comments.find((x) => x.id === i.comment_id)!;
      c.resolved_at = i.resolved === false ? null : now();
      return { comment: c };
    },
    'document.approve': (i) => {
      const d = documents.find((x) => x.id === i.document_id)!;
      approvals.filter((a) => a.project_id === d.project_id && a.target === d.kind).forEach((a) => (a.invalidated_at = now()));
      const a: Row = { id: uid(), project_id: d.project_id, target: d.kind, revision_id: i.revision_id, content_hash: revisions.find((r) => r.id === i.revision_id)!.content_hash, approved_at: now(), invalidated_at: null };
      approvals.push(a);
      return { approval: a };
    },
    'document.changes_since': (i) => ({ items: [], next_cursor: Number(i.cursor ?? 0), head: 0, has_more: false }),
    'document.index': () => ({ items: [], next_cursor: null, change_cursor: 0 }),
    'document.qa_structural_record': (i) => ({ evidence: { id: uid(), kind: 'structure', issuer_kind: 'cloud_validator', result: i.result, is_simulated: true } }),
    'catalog.search': (i) => ({ items: assets.filter((a) => !i.q || String(a.title).toLowerCase().includes(String(i.q).toLowerCase())).filter((a) => !i.kind || a.kind === i.kind), next_cursor: null }),
    'catalog.asset_get': (i) => ({ asset: assets.find((a) => a.id === i.asset_id), analyses: [], rights_evidence: [], incidents: [], usages: [] }),
    'catalog.import_batch': (i) => {
      for (const it of i.items as { asset: Row }[]) assets.push({ ...it.asset, id: uid(), tags: it.asset.tags ?? [], description: it.asset.description ?? '', origin: it.asset.origin ?? 'own', created_at: now(), versions: [], availability: [], rights_status: null, open_incidents: 0 });
      return { created: (i.items as unknown[]).length, duplicates: 0, locations: 0, records_used: assets.length, records_limit: 20000, items: [] };
    },
    'catalog.rights_overview': () => ({ incidents: [], evidence_by_status: {}, assets_without_evidence: assets.length }),
    'catalog.usage_stats': () => ({ items: [], disclaimer: 'Datos simulados.' }),
    'job.list': (i) => ({ items: jobs.filter((j) => !i.project_id || j.project_id === i.project_id).slice().reverse(), next_cursor: null }),
    'job.create': (i) => {
      const j: Row = { id: uid(), workspace_id: ws.id, project_id: i.project_id ?? null, type: i.type, status: 'waiting_device', generation: 0, required_capabilities: [], lease_expires_at: null, technical_failures: 0, is_simulated: true, eligible_devices: 1, attempts: [], artifacts: [], created_at: now() };
      jobs.push(j);
      return { job: j };
    },
    'job.cancel': (i) => {
      const j = jobs.find((x) => x.id === i.job_id)!;
      j.status = 'cancelled';
      return { job: j };
    },
    'job.device_list': () => ({ items: [] }),
    'media.status': () => ({ remote_storage: 'not_configured', max_file_bytes: 0 }),
    'media.list': () => ({ items: [] }),
    'marketing.get': (i) => ({ variants: variants.filter((v) => v.project_id === i.project_id && v.status === 'active'), history: [], combinations: [], requests: [], experiments: [], provider_available: false }),
    'marketing.variant_add': (i) => {
      const v: Row = { id: uid(), project_id: i.project_id, family: i.family, text_content: i.text_content ?? null, object_id: null, origin: 'manual', request_id: null, replaces_variant_id: null, locked: false, selected: false, status: 'active' };
      variants.push(v);
      return { variant: v };
    },
    'marketing.variant_flags': (i) => {
      const v = variants.find((x) => x.id === i.variant_id)!;
      if (i.locked !== undefined) v.locked = i.locked;
      if (i.selected !== undefined) v.selected = i.selected;
      return { variant: v };
    },
    'marketing.variant_discard': (i) => {
      variants.find((x) => x.id === i.variant_id)!.status = 'discarded';
      return { variant_id: i.variant_id, status: 'discarded' };
    },
    'marketing.deliverable_list': (i) => ({ items: deliverables.filter((d) => d.project_id === i.project_id) }),
    'marketing.deliverable_create': (i) => {
      const d: Row = { id: uid(), project_id: i.project_id, manifest_hash: hash(i), render_artifact_id: null, is_simulated: true, approval: null, qa: [] };
      deliverables.push(d);
      return { deliverable: d };
    },
    'publication.connection_list': () => ({ connections: [], capabilities: [] }),
    'publication.calendar': () => ({ items: [] }),
    'publication.list': () => ({ items: [] }),
    'analytics.metrics_query': () => ({ group_by: 'project', items: [] }),
    'agent.token_list': () => ({ items: [] }),
    'agent.context_preview': (i) => ({ context: { note: 'Contexto simulado', project_id: i.project_id } }),
    'privacy.legal_get': () => ({ documents: [{ kind: 'privacy', version: '0.1-borrador', status: 'draft', title: 'Política de privacidad (BORRADOR)', body: 'Demostración.' }], consents: [] }),
    'privacy.provider_list': () => ({ items: [], platform_processors: [] }),
    'privacy.request_list': () => ({ items: [] }),
  };

  const transport: Transport = {
    async call(key, input) {
      await new Promise((r) => setTimeout(r, 60));
      const h = handlers[key];
      if (!h) unavailable(key);
      const i = input as Record<string, unknown>;
      const result = structuredClone(once(i, key, () => h(i)));
      if (result && typeof result === 'object' && (result as { status?: string }).status === 'conflict') {
        throw new ApiError({ code: 'REVISION_CONFLICT', message: 'Hay dos versiones (demo).', retryable: false, details: result as Record<string, unknown> });
      }
      return result;
    },
  };
  return { transport };
}

function demoAuth(): AuthPort {
  const KEY = 'estudio.demo.session';
  const listeners = new Set<(s: AuthSession | null) => void>();
  const read = (): AuthSession | null => {
    try {
      const raw = sessionStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as AuthSession) : null;
    } catch {
      return null;
    }
  };
  const write = (s: AuthSession | null) => {
    try {
      if (s) sessionStorage.setItem(KEY, JSON.stringify(s));
      else sessionStorage.removeItem(KEY);
    } catch {
      // solo memoria
    }
    listeners.forEach((l) => l(s));
  };
  return {
    getSession: () => Promise.resolve(read()),
    requestEmailCode: () => Promise.resolve(),
    verifyEmailCode: (email, code) => {
      if (code !== '000000') return Promise.reject(new Error('En demostración el código es 000000.'));
      const s = { userId: 'demo', email, accessToken: 'demo', expiresAt: null };
      write(s);
      return Promise.resolve(s);
    },
    signOut: () => {
      write(null);
      return Promise.resolve();
    },
    onChange: (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
}

export function createDemoServices(): Services {
  try {
    sessionStorage.setItem('estudio.demo', '1');
  } catch {
    // sin almacenamiento de sesión
  }
  const { transport } = createDemoBackend();
  return { mode: 'demo', client: createCloudClient(transport), auth: demoAuth(), bridge: createMockBridge(), drafts: createMemoryDraftStore(), supabase: null };
}
